import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface JobCategory {
  id: number;
  title: string;
  description: string;
  requirements: string;
  status: string;
  createdBy: any;
  createdAt: string;
  cvCount: number;
  analyzedCvCount: number;
  pendingCvCount: number;
  recentCVs: any[];
}

export interface CategoryCV {
  id: number;
  applicantName: string;
  applicantEmail: string;
  applicantPhone: string;
  fileName: string;
  fileSize: number;
  analysisStatus: string;
  analysisDate: string;
  summary: string;
  totalScore?: number;
  technicalScore?: number;
  experienceScore?: number;
  educationScore?: number;
  leadershipScore?: number;
  communicationScore?: number;
  strengths?: string[];
  weaknesses?: string[];
  recommendation?: string;
  scoredAt?: string;
  uploadedBy: any;
  uploadedAt: string;
  hasFile: boolean;
}

export interface CVAnalysis {
  id: number;
  applicantName: string;
  applicantEmail: string;
  applicantPhone: string;
  fileName: string;
  fileSize: number;
  analysisStatus: string;
  analysisDate: string;
  summary: string;
  uploadedBy: any;
  uploadedAt: string;
  jobCategory: {
    id: number;
    title: string;
    description: string;
  };
}

export interface MatchResult {
  id: number;
  cv: {
    id: number;
    applicantName: string;
    applicantEmail: string;
    applicantPhone: string;
    summary: string;
  };
  matching: {
    matchScore: number;
    keyStrengths: string[];
    gaps: string[];
    assessment: string;
    priority: string;
  };
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class JobCategoryService {
  private apiUrl = `${environment.apiUrl}/job-categories`;

  constructor(private http: HttpClient) { }

  /**
   * Get all job categories with CV counts
   */
  getJobCategories(): Observable<{success: boolean, data: JobCategory[]}> {
    return this.http.get<{success: boolean, data: JobCategory[]}>(`${this.apiUrl}/categories`);
  }

  /**
   * Create a new job category
   */
  createJobCategory(categoryData: {title: string, description?: string, requirements?: string}): Observable<any> {
    return this.http.post(`${environment.apiUrl}/job-categories/categories`, categoryData);
  }

  /**
   * Update a job category
   */
  updateJobCategory(categoryId: number, categoryData: {title: string, description?: string, requirements?: string}): Observable<any> {
    return this.http.put(`${environment.apiUrl}/job-categories/categories/${categoryId}`, categoryData);
  }

  /**
   * Delete a job category
   */
  deleteJobCategory(categoryId: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/job-categories/categories/${categoryId}`);
  }

  /**
   * Get CVs for a specific job category
   */
  getCategoryCVs(categoryId: number, filters?: {status?: string, limit?: number, offset?: number}): Observable<{success: boolean, data: CategoryCV[], total: number, category: any}> {
    let params = '';
    if (filters) {
      const searchParams = new URLSearchParams();
      if (filters.status) searchParams.append('status', filters.status);
      if (filters.limit) searchParams.append('limit', filters.limit.toString());
      if (filters.offset) searchParams.append('offset', filters.offset.toString());
      params = '?' + searchParams.toString();
    }
    return this.http.get<{success: boolean, data: CategoryCV[], total: number, category: any}>(`${this.apiUrl}/categories/${categoryId}/cvs${params}`);
  }

  /**
   * Get CV analysis details
   */
  getCVAnalysis(cvId: number): Observable<{success: boolean, data: CVAnalysis}> {
    return this.http.get<{success: boolean, data: CVAnalysis}>(`${environment.apiUrl}/job-position-cvs/${cvId}/analysis`);
  }

  /**
   * Get matching results for a job category
   */
  getCategoryMatches(categoryId: number): Observable<{success: boolean, data: MatchResult[], category: any}> {
    return this.http.get<{success: boolean, data: MatchResult[], category: any}>(`${this.apiUrl}/categories/${categoryId}/matches`);
  }

  /**
   * Upload CV to a job category
   */
  uploadCVToCategory(categoryId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('cv_file', file);
    
    return this.http.post(`${environment.apiUrl}/job-position-cvs/${categoryId}/upload`, formData);
  }

  /**
   * Upload multiple CVs to a job category
   */
  uploadMultipleCVsToCategory(categoryId: number, files: File[]): Observable<any> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('cv_files', file);
    });
    
    return this.http.post(`${environment.apiUrl}/job-position-cvs/${categoryId}/upload-multiple`, formData);
  }

  /**
   * Match CVs for a job category
   */
  matchCategoryCVs(categoryId: number, description?: string): Observable<any> {
    const body = description ? { description } : {};
    return this.http.post(`${environment.apiUrl}/job-position-matching/${categoryId}/match`, body);
  }

  /**
   * Re-analyze a CV
   */
  reanalyzeCV(cvId: number): Observable<any> {
    return this.http.post(`${environment.apiUrl}/job-position-cvs/${cvId}/reanalyze`, {});
  }

  /**
   * Delete a CV
   */
  deleteCV(cvId: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/job-position-cvs/${cvId}`);
  }

  /**
   * Get CV file as Blob
   */
  getCVFile(cvId: number): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/job-position-cvs/${cvId}/file`, {
      responseType: 'blob'
    });
  }
}
